<template>
  <div v-if="loading" class="flex h-[200px] items-center justify-center">
    <Spinner :size="22" />
  </div>

  <template v-else>
    <p class="text-[14px] leading-[20px] text-gray-800">
      Each credit creates one court-ready forensic report. Buy in a bundle and
      <span class="font-medium text-red-500"
        >save up to {{ formatNumber(maxSavingsPercent) }}%.</span
      >
    </p>

    <div class="mt-3.5 flex flex-col gap-2.5">
      <button
        v-for="product in products"
        :key="product.id"
        type="button"
        class="relative cursor-pointer overflow-hidden rounded-lg border bg-white pt-[11px] pr-[15px] pb-[13px] pl-[15px] text-left shadow-[0_0.8px_2.9px_rgb(0_0_0/0.02),0_2px_7.8px_rgb(0_0_0/0.004),0_4px_18px_rgb(0_0_0/0.039)] transition-colors"
        :class="
          selectedId === product.id
            ? 'border-gray-800'
            : 'border-black/8 hover:border-gray-300'
        "
        @click="selectedId = product.id"
      >
        <span
          v-if="product.callout"
          class="absolute top-0 right-0 z-2 inline-flex items-center rounded-[0_7px_0_10px] bg-red-500 pt-[5px] pr-2.5 pb-[7px] pl-2.5 text-[12px] leading-[15px] font-medium whitespace-nowrap text-white"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 9 8"
            class="pointer-events-none absolute top-0 right-full -mr-px block h-2 w-[9px]"
          >
            <path
              d="M 0 0 L 9 0 L 9 8 L 8 8 A 8 8 0 0 0 0 0 Z"
              class="fill-red-500"
            />
          </svg>

          {{ product.callout }}

          <svg
            aria-hidden="true"
            viewBox="0 0 8 9"
            class="pointer-events-none absolute top-full right-0 -mt-px block h-[9px] w-2"
          >
            <path
              d="M 0 0 L 8 0 L 8 9 A 8 8 0 0 0 0 1 L 0 0 Z"
              class="fill-red-500"
            />
          </svg>
        </span>

        <span class="flex items-center gap-x-5">
          <span class="flex min-w-0 flex-1 flex-col gap-[3px]">
            <span class="text-[16px] leading-[22px] font-medium text-gray-800">
              {{ product.name }}
            </span>

            <span class="text-[14px] leading-[20px] text-gray-800">
              {{ product.description }}
            </span>

            <span class="flex items-center gap-2">
              <span class="text-[16px] leading-[22px] font-medium">
                <template v-if="savingsPercent(product) > 0">
                  <span class="text-red-500 line-through">{{
                    originalPrice(product)
                  }}</span>
                  <span class="text-gray-800">&nbsp;{{ price(product) }}</span>
                </template>
                <span v-else class="text-gray-800">{{ price(product) }}</span>
              </span>

              <span
                v-if="savingsPercent(product) > 0"
                class="text-[13px] leading-[20px] font-medium text-gray-500"
              >
                {{ pricePerReport(product) }} /report
              </span>
            </span>
          </span>

          <span class="flex size-5 shrink-0 items-center justify-center">
            <span
              v-if="selectedId === product.id"
              class="flex size-5 items-center justify-center rounded-full bg-green-900"
            >
              <CheckmarkIcon class="size-3 text-white" />
            </span>
          </span>
        </span>
      </button>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { CheckmarkIcon } from '@dacr/icons'

import Spinner from '@/shared/components/Spinner.vue'
import { formatNumber } from '@/shared/utils/number'
import { formatPrice } from '@/shared/utils/money'
import type { ReportProduct } from '../useBuyReportCredits'

const props = defineProps<{
  loading: boolean
  products: ReportProduct[]
}>()

const selectedId = defineModel<string | null>({ required: true })

const unitAmount = computed(
  () => props.products.find((p) => p.credits === 1)?.price.amount ?? 0
)

const undiscountedAmount = (product: ReportProduct) =>
  unitAmount.value * product.credits

const savingsPercent = (product: ReportProduct) => {
  const full = undiscountedAmount(product)
  if (!full || product.price.amount >= full) return 0
  return Math.floor((1 - product.price.amount / full) * 100)
}

const maxSavingsPercent = computed(() =>
  props.products.reduce((max, p) => Math.max(max, savingsPercent(p)), 0)
)

const price = (product: ReportProduct) =>
  formatPrice({
    amount: product.price.amount / 100,
    currency: product.price.currencyCode,
    hideDecimals: true,
  })

const originalPrice = (product: ReportProduct) =>
  formatPrice({
    amount: undiscountedAmount(product) / 100,
    currency: product.price.currencyCode,
    hideDecimals: true,
  })

const pricePerReport = (product: ReportProduct) =>
  formatPrice({
    amount: product.price.amount / product.credits / 100,
    currency: product.price.currencyCode,
  })
</script>
